import './utils/scrubCheckoutReturnUrl';
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { SimplifiedAudioProvider } from './contexts/SimplifiedAudioContext';
import IdleAnalytics from './components/performance/IdleAnalytics';
import { PostHogRoot } from './lib/analytics/posthog';
import { resolveAppVersionInfo } from './utils/appVersion';
import { installJourneyTiming } from './utils/journeyTiming';
import { getNativeDeviceProfile } from './utils/nativeDevice';
import { scheduleAppShellHandoff } from './utils/appShellHandoff';
import { runAfterFirstPaint } from './utils/afterFirstPaint';
import { shouldSuppressSensitiveAuthTelemetry } from './utils/urlPrivacy';
import { installAdExcludedPageGuard } from './utils/adExcludedPageGuard';

declare global {
  interface Window {
    __JP_NATIVE_RUNTIME__?: boolean;
  }
}

const NATIVE_DEVICE = getNativeDeviceProfile();
const IS_NATIVE_RUNTIME = NATIVE_DEVICE.isNativeRuntime;
const NATIVE_SERVICE_WORKER_CLEANUP_KEY = 'jp_native_sw_cleanup_v3';
const LOCAL_DEV_SERVICE_WORKER_CLEANUP_KEY = 'jp_local_dev_sw_cleanup_v1';
const NATIVE_VIEWPORT_META =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

function applyNativeViewportPolicy() {
  if (typeof document === 'undefined') return;
  const viewportMeta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!viewportMeta) return;
  viewportMeta.setAttribute('content', NATIVE_VIEWPORT_META);
}

// api/ssr.ts strips Helmet's data-rh marker from the server-rendered canonical;
// restore it so react-helmet-async replaces that tag instead of adding a second one.
if (typeof document !== 'undefined') {
  document.querySelectorAll('link[rel="canonical"]:not([data-rh])').forEach((el) => el.setAttribute('data-rh', 'true'));
}

if (typeof document !== 'undefined' && IS_NATIVE_RUNTIME) {
  document.documentElement.classList.add('native-runtime');
  document.body.classList.add('native-runtime');
  applyNativeViewportPolicy();

  // Onboarding artwork must be ready before the app shell fades. Native only:
  // the web homepage never shows it (#1336).
  const onboardingArt = document.createElement('link');
  onboardingArt.rel = 'preload';
  onboardingArt.as = 'image';
  onboardingArt.type = 'image/webp';
  onboardingArt.href = '/mascots/party-animals-group.webp';
  onboardingArt.setAttribute('fetchpriority', 'high');
  document.head.appendChild(onboardingArt);

  if (NATIVE_DEVICE.isNativeIOS) {
    document.documentElement.classList.add('native-ios');
    document.body.classList.add('native-ios');
  }

  if (NATIVE_DEVICE.isNativeAndroid) {
    // Sessions and the podcast keep playing with the phone locked.
    void import('./lib/native/sessionAudioKeepAlive').then((m) => m.installSessionAudioKeepAlive());
  }

  if (NATIVE_DEVICE.isNativeIPad) {
    document.documentElement.classList.add('native-ipad');
    document.body.classList.add('native-ipad');
  }

  if (NATIVE_DEVICE.isNativePhone) {
    document.documentElement.classList.add('native-phone', 'native-compact');
    document.body.classList.add('native-phone', 'native-compact');
  }
}

if (typeof window !== 'undefined') {
  window.__JP_NATIVE_RUNTIME__ = IS_NATIVE_RUNTIME;

  if (!import.meta.env.PROD) {
    void disableLocalDevServiceWorkersOnce();
  }

  if (IS_NATIVE_RUNTIME) {
    // Run cleanup at startup to avoid one more launch on stale cached bundles.
    void disableNativeServiceWorkersOnce();
  }
}

function normalizeEntryPath(value: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value;
  }
}

async function clearServiceWorkerState(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  let hadServiceWorkerState = false;

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
    if (registrations.length > 0 || Boolean(navigator.serviceWorker.controller)) {
      hadServiceWorkerState = true;
    }
    await Promise.all(
      registrations.map((registration) => registration.unregister().catch(() => false))
    );
  }

  if ('caches' in window) {
    const cacheNames = await caches.keys().catch(() => []);
    if (cacheNames.length > 0) {
      hadServiceWorkerState = true;
    }
    await Promise.all(cacheNames.map((name) => caches.delete(name).catch(() => false)));
  }

  return hadServiceWorkerState;
}

function hasCompletedNativeServiceWorkerCleanup(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NATIVE_SERVICE_WORKER_CLEANUP_KEY) === '1';
  } catch {
    return false;
  }
}

function markNativeServiceWorkerCleanupComplete(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NATIVE_SERVICE_WORKER_CLEANUP_KEY, '1');
  } catch {
    // Ignore localStorage failures (private mode / storage restrictions).
  }
}

function hasCompletedLocalDevServiceWorkerCleanup(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(LOCAL_DEV_SERVICE_WORKER_CLEANUP_KEY) === '1';
  } catch {
    return false;
  }
}

function markLocalDevServiceWorkerCleanupComplete(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LOCAL_DEV_SERVICE_WORKER_CLEANUP_KEY, '1');
  } catch {
    // Ignore storage failures (private mode / storage restrictions).
  }
}

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

async function disableLocalDevServiceWorkersOnce(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (import.meta.env.PROD) return;
  if (!isLocalDevHost()) return;
  if (!('serviceWorker' in navigator)) return;
  if (hasCompletedLocalDevServiceWorkerCleanup()) return;

  try {
    const hadServiceWorkerState = await clearServiceWorkerState();
    markLocalDevServiceWorkerCleanupComplete();

    // Local dev must always use the Vite source graph, not a previous PWA build.
    if (hadServiceWorkerState && navigator.serviceWorker.controller) {
      const url = new URL(window.location.href);
      url.searchParams.set('local_sw_reset_at', String(Date.now()));
      window.location.replace(url.toString());
    }
  } catch (error) {
    console.warn('[Dev Bootstrap] Failed to reset local service worker state:', error);
  }
}

async function disableNativeServiceWorkersOnce(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!IS_NATIVE_RUNTIME) return;
  if (!('serviceWorker' in navigator)) return;
  if (hasCompletedNativeServiceWorkerCleanup()) return;

  try {
    const hadServiceWorkerState = await clearServiceWorkerState();
    markNativeServiceWorkerCleanupComplete();

    // If a stale SW was controlling this page, force a single reload so new assets win immediately.
    if (hadServiceWorkerState && navigator.serviceWorker.controller) {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('native_sw_reset_at')) {
        url.searchParams.set('native_sw_reset_at', String(Date.now()));
        window.location.replace(url.toString());
      }
    }
  } catch (error) {
    console.warn('[Native Bootstrap] Failed to reset service worker state:', error);
  }
}

function scheduleIdleWork(task: () => void) {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => task());
  } else {
    setTimeout(() => task(), 1500);
  }
}

// Global client recovery for stale SW bundles and hydration/chunk failures
(function setupClientBundleRecovery() {
  if (typeof window === 'undefined') return;
  if (shouldSuppressSensitiveAuthTelemetry(window.location.href)) return;

  const RECOVERY_KEY = 'jp_client_bundle_recovery_at';
  const STALE_CHECK_KEY = 'jp_stale_bundle_check_at';
  const RECOVERY_COOLDOWN_MS = 60_000;
  const STALE_CHECK_COOLDOWN_MS = 5 * 60_000;

  function extractMessage(err: unknown): string {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err) {
      return String((err as any).message);
    }
    return '';
  }

  function isRecoverableRuntimeError(err: unknown): boolean {
    const msg = extractMessage(err);
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Minified React error #418') ||
      msg.includes('Minified React error #423') ||
      msg.includes('Hydration failed') ||
      msg.includes('does not match server-rendered HTML')
    );
  }

  async function triggerRecovery(reason: string) {
    const now = Date.now();
    const lastRecoveryAt = Number(sessionStorage.getItem(RECOVERY_KEY) || '0');
    if (Number.isFinite(lastRecoveryAt) && now - lastRecoveryAt < RECOVERY_COOLDOWN_MS) {
      return;
    }

    sessionStorage.setItem(RECOVERY_KEY, String(now));
    console.warn('[Client Recovery] resetting stale client bundle:', reason);

    await clearServiceWorkerState();

    const url = new URL(window.location.href);
    url.searchParams.set('client_recovered_at', String(now));
    window.location.replace(url.toString());
  }

  window.addEventListener('error', (e) => {
    if (isRecoverableRuntimeError((e as any).error ?? (e as any).message)) {
      void triggerRecovery('window-error');
    }
  });

  window.addEventListener('unhandledrejection', (e) => {
    if (isRecoverableRuntimeError((e as any).reason)) {
      void triggerRecovery('unhandled-rejection');
    }
  });

  if (!import.meta.env.PROD) return;
  if (IS_NATIVE_RUNTIME) return;

  const lastStaleCheckAt = Number(sessionStorage.getItem(STALE_CHECK_KEY) || '0');
  if (Number.isFinite(lastStaleCheckAt) && Date.now() - lastStaleCheckAt < STALE_CHECK_COOLDOWN_MS) {
    return;
  }
  sessionStorage.setItem(STALE_CHECK_KEY, String(Date.now()));

  // Detect when the running entry chunk is stale vs the latest deployed entry.
  void (async () => {
    const currentEntryScript = document.querySelector<HTMLScriptElement>(
      'script[type="module"][src*="/assets/index-"]'
    );
    const currentEntryPath = normalizeEntryPath(currentEntryScript?.getAttribute('src') ?? null);
    if (!currentEntryPath) return;

    // Older installed service workers may still have index.html in their
    // precache. A unique, non-ignored query keeps this probe network-owned
    // while the new worker replaces those installations.
    const probePath = `/?jp_bundle_probe=${encodeURIComponent(String(Date.now()))}`;

    const response = await fetch(probePath, {
      cache: 'no-store',
      credentials: 'same-origin',
    }).catch(() => null);
    if (!response?.ok) return;

    const html = await response.text();
    const latestEntryMatch = html.match(
      /<script[^>]*type="module"[^>]*src="([^"]*\/assets\/index-[^"]+\.js)"/i
    );
    const latestEntryPath = normalizeEntryPath(latestEntryMatch?.[1] ?? null);

    if (!latestEntryPath || latestEntryPath === currentEntryPath) return;

    await triggerRecovery(`stale-entry:${currentEntryPath}->${latestEntryPath}`);
  })();
})();

// Before the first render: listens for taps and starts app_launch on Home.
installJourneyTiming();

// Before the router: in-app navigation into teen/student guides becomes a
// full page load, so ad pixels from earlier pages never run on them.
if (!IS_NATIVE_RUNTIME) installAdExcludedPageGuard();

const root = document.getElementById('root');
const container = document.getElementById('app');

if (root && container) {
  // Web hydrates the prerendered markup (content-first for crawlers). Native
  // renders fresh: it holds a loading state on the landing route until auth
  // resolves (see Homepage), which would not match the prerendered landing HTML,
  // so hydrating there would mismatch. Fresh render + the kept loading overlay
  // (html.native-runtime rule) cover the native cold-boot cleanly.
  const shouldHydrate = root.getAttribute('data-ssr') === 'true' && !IS_NATIVE_RUNTIME;
  const app = (
    <React.StrictMode>
      <PostHogRoot>
        <SimplifiedAudioProvider>
          <App />
          <IdleAnalytics />
        </SimplifiedAudioProvider>
      </PostHogRoot>
    </React.StrictMode>
  );

  if (shouldHydrate) {
    // Paint the server HTML before the long hydration task (#1301).
    runAfterFirstPaint(() => {
      hydrateRoot(container, app, {
        // Surface the component stack for recoverable hydration errors
        // (e.g. React #421/#418) so they are diagnosable from production
        // consoles and Sentry breadcrumbs instead of a bare error number.
        onRecoverableError(error, errorInfo) {
          console.error(
            '[hydration] recoverable error:',
            error,
            errorInfo?.componentStack ?? ''
          );
        },
      });
    });
  } else {
    createRoot(container).render(app);
  }

  if (!shouldHydrate) {
    scheduleAppShellHandoff(root);
  }
}

scheduleIdleWork(async () => {
  const suppressThirdPartyTelemetry =
    typeof window !== 'undefined' &&
    shouldSuppressSensitiveAuthTelemetry(window.location.href);

  if (!suppressThirdPartyTelemetry) {
    // Stripe.js is not loaded here: checkout components load it on demand
    // through src/utils/stripe.ts, so ordinary pages skip the 255 KB script.
    const [{ initSentry }, { reportWebVitals }, appVersion] = await Promise.all([
      import('./utils/sentry'),
      import('./utils/reportWebVitals'),
      // Release stamp for Sentry; a slow native bridge falls back to none.
      resolveAppVersionInfo(1000),
    ]);

    // Web only: initSentry is a no-op on native, without a DSN, or after an analytics opt-out.
    if (!IS_NATIVE_RUNTIME) void initSentry({ release: appVersion?.appVersion });

    if (import.meta.env.PROD) {
      reportWebVitals();
    }
  }

  if (IS_NATIVE_RUNTIME) {
    return;
  }

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    const { registerSW } = await import('virtual:pwa-register');
    const updateSW = registerSW({
      onNeedRefresh() {
        // Don't block the user (Story 20.2 fix)
        console.log('New content is available; please refresh.');

        // Dispatch event for UI to handle if desired
        window.dispatchEvent(new CustomEvent('sw-update-available', {
          detail: {
            message: 'New version available',
            action: () => updateSW(true)
          }
        }));

        // Auto-apply the update so users don't stay on stale chunks.
        window.setTimeout(() => {
          updateSW(true);
        }, 800);
      },
      onOfflineReady() {
        console.log('App is ready to work offline');
      },
      onRegistered(registration) {
        console.log('Service Worker registered:', registration);
      },
      onRegisterError(error) {
        console.error('Service Worker registration failed:', error);
      },
    });
  }
});
